import React, { useState, useEffect, useContext } from "react";
import { Row, Col, Breadcrumb, Card, message, Empty, Spin, Input, Select, Button, Pagination } from "antd";
import { DashboardOutlined, CommentOutlined } from "@ant-design/icons";
import { BiNotepad } from "react-icons/bi";
import { Link } from "react-router-dom";
import Header from "../../components/dashboard/header";
import { useGetPaginatedMemosAwaitingMyComment } from "../../hooks/memo/memoQuery";
import { useFetchAllFinancialYears } from "../../hooks/financialYear/financialYearQuery";
import { userStore } from "../../store/userStore";
import RefreshContext from "../../context/refreshContext";
import PreviewMemo from "../../components/memo/PreviewMemo";

const { Search } = Input;
const { Option } = Select;

export default function MemoRecommendation() {
    const [memos, setMemos] = useState([]);
    const [filteredMemos, setFilteredMemos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [financialYears, setFinancialYears] = useState([]);
    const [selectedYear, setSelectedYear] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const user = userStore(state => state.user);
    const { flag } = useContext(RefreshContext);

    const getPaginatedMemosAwaitingMyComment = useGetPaginatedMemosAwaitingMyComment();
    const { years: financialYearsData, loading: yearsLoading } = useFetchAllFinancialYears(flag);

    useEffect(() => {
        if (financialYearsData && financialYearsData.length > 0) {
            setFinancialYears(financialYearsData);

            // Set current year as default
            const currentYear = financialYearsData.find(year => year.currentYear);
            if (currentYear) {
                setSelectedYear(currentYear.id);
            } else {
                setSelectedYear(financialYearsData[0].id);
            }
        }
    }, [financialYearsData]);

    useEffect(() => {
        const fetchMemos = async () => {
            if (!selectedYear) return;

            setLoading(true);
            try {
                const response = await getPaginatedMemosAwaitingMyComment(currentPage - 1, pageSize, selectedYear);
                setMemos(response?.content || []);
                setFilteredMemos(response?.content || []);
                setTotalElements(response?.totalElements || 0);
                setTotalPages(response?.totalPages || 0);
            } catch (error) {
                console.error("Error fetching memos:", error);
                message.error("Failed to load memos awaiting recommendation");
            } finally {
                setLoading(false);
            }
        };

        fetchMemos();
    }, [selectedYear, currentPage, pageSize, flag]);

    useEffect(() => {
        // Filter memos based on search term
        if (!searchTerm) {
            setFilteredMemos(memos);
        } else {
            const filtered = memos.filter(memo =>
                memo.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                memo.creator?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                memo.department?.title?.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredMemos(filtered);
        }
    }, [searchTerm, memos]);

    const onYearChange = (value) => {
        setSelectedYear(value);
        setCurrentPage(1);
    };

    const onPageChange = (page, size) => {
        setCurrentPage(page);
        setPageSize(size);
    };

    const onSearch = (value) => {
        setSearchTerm(value);
    };

    return (
        <>
            <div className="main-container">
                <Header />
                <div className="main-body">
                    <div className="info-bar">
                        <Breadcrumb>
                            <Breadcrumb.Item>
                                <DashboardOutlined className="dashboard-icon" />
                                <Link to={"/dashboard"}>Dashboard</Link>
                            </Breadcrumb.Item>
                            <Breadcrumb.Item>
                                <BiNotepad />
                                <Link to={"/dashboard"}>Memo</Link>
                            </Breadcrumb.Item>
                            <Breadcrumb.Item>
                                <CommentOutlined />
                                <Link to="/dashboard/memo/recommendation">Recommendation</Link>
                            </Breadcrumb.Item>
                        </Breadcrumb>
                        <div style={{ display: 'flex', gap: '1em' }}>
                            <Select
                                style={{ width: 200 }}
                                placeholder="Select Financial Year"
                                value={selectedYear}
                                onChange={onYearChange}
                            >
                                {financialYears.map(year => (
                                    <Option key={year.id} value={year.id}>
                                        {year.value}
                                    </Option>
                                ))}
                            </Select>
                            <Search
                                placeholder="Search memos..."
                                style={{ width: 300 }}
                                onSearch={onSearch}
                                onChange={(e) => onSearch(e.target.value)}
                                allowClear
                            />
                        </div>
                    </div>

                    <div style={{ padding: '20px' }}>
                        <Card
                            title="Memos Awaiting My Recommendation"
                            bordered={false}
                            style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
                        >
                            <Spin spinning={loading}>
                                {filteredMemos.length === 0 ? (
                                    <Empty
                                        description={
                                            loading
                                                ? "Loading memos..."
                                                : "No memos awaiting your recommendation"
                                        }
                                        style={{ padding: '40px 0' }}
                                    />
                                ) : (
                                    <>
                                        <div className="memo-list">
                                            {filteredMemos.map((memo) => (
                                                <Card
                                                    key={memo.id}
                                                    size="small"
                                                    style={{
                                                        marginBottom: 16,
                                                        border: '1px solid #f0f0f0',
                                                        borderRadius: '8px'
                                                    }}
                                                >
                                                    <Row>
                                                        <Col span={18}>
                                                            <div style={{ marginBottom: 8 }}>
                                                                <strong style={{ fontSize: '16px' }}>
                                                                    {memo.title}
                                                                </strong>
                                                            </div>
                                                            <div style={{ color: '#666', marginBottom: 4 }}>
                                                                <strong>Creator:</strong> {memo.creator?.name} ({memo.creator?.email})
                                                            </div>
                                                            <div style={{ color: '#666', marginBottom: 4 }}>
                                                                <strong>Department:</strong> {memo.department?.title}
                                                            </div>
                                                            <div style={{ color: '#666', marginBottom: 4 }}>
                                                                <strong>Date: </strong> {memo.date[0]}-{String(memo.date[1]).padStart(2,0)}-{memo.date[2]}
                                                            </div>
                                                            <div style={{ color: '#666', marginBottom: 4 }}>
                                                                <strong>Financial Year:</strong> {memo.financialYear?.year}
                                                            </div>
                                                            {memo.isAwaitingCommentRequest ? (
                                                                <div style={{ color: '#1890ff', marginBottom: 4 }}>
                                                                    <strong>Status:</strong> Awaiting comment from {memo.awaitingCommentFromUser?.name}
                                                                </div>
                                                            ) : (
                                                                <div style={{ color: '#52c41a', marginBottom: 4 }}>
                                                                    <strong>Status:</strong> {memo.comment || 'Awaiting approval'}
                                                                </div>
                                                            )}
                                                            {memo.submitted && (
                                                                <div style={{ color: '#666', marginBottom: 4 }}>
                                                                    <strong>Submitted:</strong> Yes
                                                                </div>
                                                            )}
                                                            {memo.approved !== null && (
                                                                <div style={{ color: memo.approved ? '#52c41a' : '#ff4d4f', marginBottom: 4 }}>
                                                                    <strong>Approved:</strong> {memo.approved ? 'Yes' : 'No'}
                                                                </div>
                                                            )}
                                                            {memo.amount && (
                                                                <div style={{ color: '#666' }}>
                                                                    <strong>Amount:</strong> ₦{memo.amount?.toLocaleString()}
                                                                </div>
                                                            )}
                                                        </Col>
                                                        <Col span={6} style={{ textAlign: 'right' }}>
                                                            <PreviewMemo memo={memo} />
                                                        </Col>
                                                    </Row>
                                                </Card>
                                            ))}
                                        </div>
                                        <div style={{ marginTop: 20, textAlign: 'center' }}>
                                            <Pagination
                                                current={currentPage}
                                                total={totalElements}
                                                pageSize={pageSize}
                                                onChange={onPageChange}
                                                onShowSizeChange={onPageChange}
                                                showSizeChanger
                                                showQuickJumper
                                                showTotal={(total, range) =>
                                                    `${range[0]}-${range[1]} of ${total} items`
                                                }
                                                pageSizeOptions={['5', '10', '20', '50']}
                                            />
                                        </div>
                                    </>
                                )}
                            </Spin>
                        </Card>
                    </div>
                </div>
            </div>
        </>
    );
}
